'use client';
import { useEffect, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';

/** How long a touch must rest before it counts as a long press.
 *  @internal Exported for the press timing in `ContextMenuTrigger.test.tsx` and the caller tests. */
export const LONG_PRESS_MS = 500;
/** How far the finger may wander meanwhile before the gesture is a scroll or a drag instead. */
const LONG_PRESS_SLOP_PX = 12;
/** How long after a long press its release click and the browser's own context menu stay suppressed. */
const LONG_PRESS_QUIET_MS = 1000;

/** Presses an inner long-press owner already took. An enclosing owner (a surface around a row, a table
 *  around a card) sees the same native event bubble up next and must not start a second gesture on it. */
const claimed = new WeakSet<Event>();

export interface LongPressPoint { x: number; y: number }

type Pending<T> = { subject: T; x: number; y: number; element: HTMLElement; timer: ReturnType<typeof setTimeout> | null };

/** The one touch long-press gesture of the web app. `ContextMenuTrigger` turns it into a menu; nothing
 *  else owns a press timer of its own.
 *
 *  A primary touch or pen press on something `subjectOf` names arms a timer. Moving past the slop, a
 *  `pointercancel` (the browser taking the touch for a scroll) or an early release disarms it. When it
 *  fires, `onLongPress` gets the subject and the press point; the held element carries `data-pressing`
 *  until the finger lifts, so its stylesheet can stop a text selection from starting under it. The
 *  long press's own release click is swallowed when it lands on the same subject; the next press anywhere
 *  ends that wait, because a click after it is a new gesture (Android may send no release click at all,
 *  and the row the reader then taps can click a link inside the subject). A `contextmenu` the
 *  browser raises for the same hold (Android does, iOS does not) either fires the press at once or, after
 *  the timer, is swallowed — one open, never two. Mice never long-press here: they have a right button.
 *
 *  The caller attaches the pointer handlers and routes its click capture and `contextmenu` through
 *  `releaseClick` and `contextMenu` (and a caller that opens a menu its mousedown capture through
 *  `releaseMouseDown`), which answer whether the event belonged to the gesture. */
export function useLongPress<T extends string>(options: {
  enabled: boolean;
  /** The subject an event target belongs to, or null when a press there is not this gesture's. Read at
   *  the press, and again for the release click, which is swallowed only on the same subject. */
  subjectOf: (target: Element) => T | null;
  onLongPress: (subject: T, point: LongPressPoint) => void;
}) {
  const latest = useRef(options);
  useEffect(() => { latest.current = options; });
  const pending = useRef<Pending<T> | null>(null);
  const suppress = useRef<{ subject: T; until: number } | null>(null);
  /** Removes the document listener that ends `suppress` at the next press; set while `suppress` is. */
  const unwatch = useRef<(() => void) | null>(null);
  const lastFired = useRef(0);

  const endSuppress = () => {
    suppress.current = null;
    unwatch.current?.();
    unwatch.current = null;
  };

  const release = () => {
    const press = pending.current;
    pending.current = null;
    if (!press) return;
    if (press.timer) clearTimeout(press.timer);
    press.element.removeAttribute('data-pressing');
  };
  const fire = (press: Pending<T>) => {
    if (press.timer) clearTimeout(press.timer);
    press.timer = null;
    const at = Date.now();
    lastFired.current = at;
    endSuppress();
    suppress.current = { subject: press.subject, until: at + LONG_PRESS_QUIET_MS };
    // Opening the menu can scroll its trigger away. The browser's compatibility release then lands on
    // a different element, so capture that release at the document, before it moves focus or clicks it.
    // Any new press ends suppression; keyboard/programmatic clicks (detail 0) are never this release.
    const releaseMouse = (event: MouseEvent) => {
      if (event.detail === 0 || !suppress.current || Date.now() >= suppress.current.until) return;
      event.preventDefault();
      event.stopPropagation();
      if (event.type === 'click') endSuppress();
    };
    document.addEventListener('pointerdown', endSuppress, true);
    document.addEventListener('mousedown', releaseMouse, true);
    document.addEventListener('click', releaseMouse, true);
    unwatch.current = () => {
      document.removeEventListener('pointerdown', endSuppress, true);
      document.removeEventListener('mousedown', releaseMouse, true);
      document.removeEventListener('click', releaseMouse, true);
    };
    latest.current.onLongPress(press.subject, { x: press.x, y: press.y });
  };
  // A press held across unmount must not fire into a gone surface, nor its wait outlive it.
  useEffect(() => () => { release(); endSuppress(); }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (!latest.current.enabled || claimed.has(event.nativeEvent)) return;
    if ((event.pointerType !== 'touch' && event.pointerType !== 'pen') || !event.isPrimary || event.button !== 0) return;
    // React bubbles a portal's events (a dialog opened from a card) through the card; a press there is not
    // on this element.
    if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target)) return;
    const subject = latest.current.subjectOf(event.target);
    if (subject === null) return;
    claimed.add(event.nativeEvent);
    release();
    const element = event.currentTarget;
    element.setAttribute('data-pressing', '');
    const press: Pending<T> = { subject, x: event.clientX, y: event.clientY, element, timer: null };
    press.timer = setTimeout(() => fire(press), LONG_PRESS_MS);
    pending.current = press;
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const press = pending.current;
    if (!press?.timer) return;
    if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > LONG_PRESS_SLOP_PX) release();
  };
  /** Swallows the long press's own release click; true when it did. Only the first click after a press,
   *  and before any new press, is considered, so a tap elsewhere (a menu row rendered inside the element)
   *  and whatever that tap's action clicks pass untouched. */
  const releaseClick = (event: ReactMouseEvent<HTMLElement>): boolean => {
    const quiet = suppress.current;
    endSuppress();
    if (!quiet || Date.now() >= quiet.until || !(event.target instanceof Element)) return false;
    if (latest.current.subjectOf(event.target) !== quiet.subject) return false;
    event.stopPropagation();
    event.preventDefault();
    return true;
  };
  /** Cancels the `mousedown` ahead of that release click, so it moves no focus; true when it did. A touch
   *  held past the timer and lifted is still a tap to Chromium's and Safari's gesture recognisers, and the
   *  focus their compatibility `mousedown` would give a button under the finger counts as focus leaving the
   *  menu the press just opened, which dismisses it. The wait stays armed for the click that follows. */
  const releaseMouseDown = (event: ReactMouseEvent<HTMLElement>): boolean => {
    const quiet = suppress.current;
    if (!quiet || Date.now() >= quiet.until || !(event.target instanceof Element)) return false;
    if (latest.current.subjectOf(event.target) !== quiet.subject) return false;
    event.preventDefault();
    return true;
  };
  /** Takes a `contextmenu` that belongs to a held or just-finished long press; true when it did. A menu
   *  event another handler already took (`defaultPrevented`) is never this gesture's. */
  const contextMenu = (event: ReactMouseEvent<HTMLElement>): boolean => {
    if (event.defaultPrevented) return false;
    const press = pending.current;
    if (press?.timer) {
      fire(press);
      event.preventDefault();
      return true;
    }
    if (Date.now() - lastFired.current < LONG_PRESS_QUIET_MS) {
      event.preventDefault();
      return true;
    }
    return false;
  };
  return { onPointerDown, onPointerMove, onPointerUp: release, onPointerCancel: release, releaseMouseDown, releaseClick, contextMenu };
}
